import axios from "axios"

import { jwtDecode } from "jwt-decode"
import { toast } from "react-toastify"
import {
  encryptedGETParams,
  isEmpty
} from "../utils/commonFunction"
import { getAuthtoken } from "../utils/tokens"
import jwtDefaultConfig from "../utils/auth/jwt/jwtDefaultConfig"
import { store } from "../redux/store"
import { getAccessToken as getAccessTokenV2, getUserData as getUserDataV2 } from "../utils/authV2/tokenManager"
import {
  forceLogoutToLogin,
  handleTokenExpiredSuccessResponse,
  handleUnauthorizedWithRefresh
} from "../utils/authV2/tokenRefreshService"
import { applyRouteContextHeaders, getRouteContext } from "../utils/routeContext"
import { getPublicApiKeyV2 } from "./endpointUtils/authV2"
import { setPublicKey } from "../redux/actions/userSessionAction"

const jwtDefault = { ...jwtDefaultConfig }

const getPublicAPIKeyFromStoreRef = () => {
  return store.getState().userSession.publicApiKey
}
const webp_support = () => {
  if (typeof document === "undefined") {
    return false
  }
  try {
    const elem = document.createElement("canvas")
    if (!!(!isEmpty(elem) && elem.getContext && elem.getContext("2d"))) {
      // was able or not to get WebP representation
      return elem.toDataURL("image/webp").indexOf("data:image/webp") === 0
    } else {
      // very old browser like IE 8, canvas not supported
      return false
    }
  } catch (error) {
    return false
  }
}

// Function to prepare headers
export const getPreparedHeaders = () => {
  const { project, module } = getRouteContext()
  const token = localStorage?.getItem("token") || ""
  let username = ""
  let uniqueId = ""

  try {
    if (token) {
      const userDetails = jwtDecode(token)
      username = userDetails?.username
    }
    uniqueId = localStorage?.getItem("uniqueId") || ""
  } catch (error) {
    toast("Failed to decode token or retrieve user details:", {
      hideProgressBar: true,
      type: "error"
    })
  }

  return {
    project: project || "",
    module: module || "",
    token,
    username,
    uniqueId
  }
}

export const prepareHeaders = (headers) => {
  const { token, username, uniqueId } = getPreparedHeaders()

  applyRouteContextHeaders(headers)

  if (username) headers.set("username", username)
  if (token) headers.set("authorization", `Bearer ${token}`)
  if (uniqueId) headers.set("Unique_id", uniqueId)

  return headers
}
let isAlreadyFetchingAccessToken = false
let isAlreadyFetchingPublicApiKey = false
let publicApiKeyPromise = null

const refreshPublicApiKey = async () => {
  if (isAlreadyFetchingPublicApiKey && publicApiKeyPromise) {
    return publicApiKeyPromise
  }

  isAlreadyFetchingPublicApiKey = true
  publicApiKeyPromise = getPublicApiKeyV2()
    .then((res) => {
      if (res?.status === 200) {
        store.dispatch(setPublicKey(res.data))
      }
      return res
    })
    .finally(() => {
      isAlreadyFetchingPublicApiKey = false
      publicApiKeyPromise = null
    })

  return publicApiKeyPromise
}
// Axios instance configuration
const rawInstanceConfig = {
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    // Add any default headers here if needed
  }
}

export const rawInstance = axios.create(rawInstanceConfig)

// Adding request interceptor to set the headers
rawInstance.interceptors.request.use(
  (config) => {
    const preparedHeaders = new Headers()
    prepareHeaders(preparedHeaders)
    preparedHeaders.forEach((value, key) => {
      config.headers[key] = value
    })

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const tokenInstanceConfig = {
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    Accept: "application/json",
    "Content-Type": "application/json",
    WebpSupport: webp_support() ? "yes" : "no"
  }
}

export const tokenInstance = axios.create(tokenInstanceConfig)
tokenInstance.interceptors.request.use(async (tokenInstanceConfig) => {
  tokenInstanceConfig.headers.authtoken = getAuthtoken()

  // Support both legacy and V2 authentication tokens
  const legacyAccessToken = localStorage.getItem("accessToken")
  const v2AccessToken = getAccessTokenV2()
  const accessToken = legacyAccessToken || v2AccessToken

  const userDataV2 = getUserDataV2()

  let role
  const unique_id = localStorage.getItem("uniqueId") || userDataV2?.id

  // Derive role from legacy JWT if available, otherwise from V2 user data
  if (legacyAccessToken) {
    try {
      const roleDetails = jwtDecode(legacyAccessToken)
      role = roleDetails?.userData?.role
    } catch (error) {
      // ignore decode errors for legacy token
    }
  } else if (userDataV2?.role) {
    role = userDataV2.role
  }

  applyRouteContextHeaders(tokenInstanceConfig.headers)

  if (unique_id) {
    tokenInstanceConfig.headers.unique_id = unique_id
  }
  if (role) {
    tokenInstanceConfig.headers.role = role
  }

  if (userDataV2?.email || userDataV2?.username) {
    tokenInstanceConfig.headers.username = userDataV2.email || userDataV2.username
  } else if (legacyAccessToken) {
    try {
      tokenInstanceConfig.headers.username = jwtDecode(legacyAccessToken).userData.email
    } catch (error) {

    }
  }

  if (userDataV2?.user_type) {
    tokenInstanceConfig.headers["user-type"] = userDataV2.user_type
  }

  if (accessToken) {
    // ** eslint-disable-next-line no-param-reassign
    tokenInstanceConfig.headers.Authorization = `${jwtDefault.tokenType} ${accessToken}`
  }

  if (tokenInstanceConfig.method.toLowerCase() == "get") {
    const publicApiKey = getPublicAPIKeyFromStoreRef()
    if (publicApiKey && tokenInstanceConfig.params) {
      // ***note*** : Exclude meter from encryption only for tariff-info endpoint
      const isTariffInfoEndpoint = tokenInstanceConfig.url && tokenInstanceConfig.url.includes("/api/hes/gas/tariff-info/")
      const excludeFromEncryption = isTariffInfoEndpoint ? ["meter"] : []
      tokenInstanceConfig.params = await encryptedGETParams(publicApiKey, {
        ...tokenInstanceConfig.params
      }, excludeFromEncryption)
      // Mark request so we can selectively retry if key rotates and server returns 400
      tokenInstanceConfig.headers["x-encrypted-params"] = "1"
    }
  }

  return tokenInstanceConfig
})

tokenInstance.interceptors.response.use(
  async function (response) {
    // Some APIs return HTTP 200 with { response_code: 401, error_message: "token expired" }
    return handleTokenExpiredSuccessResponse(response, tokenInstance)
  },
  async function (error) {
    const { config, response } = error
    const originalRequest = config
    const status = response?.status
    const hasV2Session = getAccessTokenV2() && getUserDataV2()

    // If the app stays open for a long time, encrypted GET param requests can fail
    // when the server rotates the public key. Re-fetch key and retry once.
    if (
      status === 400 &&
      originalRequest &&
      !originalRequest._retryPublicApiKey &&
      String(originalRequest?.method || "").toLowerCase() === "get" &&
      originalRequest?.headers?.["x-encrypted-params"] === "1" &&
      originalRequest?.params
    ) {
      originalRequest._retryPublicApiKey = true
      try {
        const keyRes = await refreshPublicApiKey()
        if (keyRes?.status === 200) {
          return tokenInstance(originalRequest)
        }
      } catch (e) {
        console.error("error in refreshPublicApiKey", e)
      }
    }

    // Access token expired / unauthorized → refresh → retry
    if (hasV2Session && (status === 401 || Number(response?.data?.response_code) === 401)) {
      try {
        return await handleUnauthorizedWithRefresh(error, tokenInstance)
      } catch (refreshError) {
        // forceLogoutToLogin already ran when refresh itself failed
        return Promise.reject(refreshError)
      }
    }

    // Legacy session only — no V2 refresh path
    if (!hasV2Session && (status === 403 || status === 401)) {
      forceLogoutToLogin("legacy unauthorized")
    }

    return Promise.reject(error)
  }
)
